/**
 * Shared client-side publishing-status derivation.
 *
 * This mirrors the server's decision table (there is a server unit test on the
 * same fixture in packages/publishing-status-contract/fixture.json, and a
 * matching client test in publishing-status.spec.ts). Keep the two in lockstep.
 */

export type PublishingStatus =
  | "scheduled"
  | "queued_publishing"
  | "posting"
  | "retrying"
  | "delayed"
  | "manual_publishing"
  | "posted"
  | "imported"
  | "failed"
  | "potential_error"
  | "canceled";

export type DateLike = string | number | Date | null | undefined;

/**
 * Canonical raw fields the fallback derivation reads. All optional so callers
 * can pass a post object directly. Date fields are typed loosely (`unknown`)
 * so callers can pass moment inputs / model dates without casting — {@link toMs}
 * coerces them at runtime.
 */
export interface PublishingStatusInput {
  /** Draft-backed post — short-circuits to a benign status (never an error). */
  isDraft?: boolean | null;
  isCanceled?: boolean | null;
  isImported?: boolean | null;
  isSuccess?: boolean | null;
  isPosting?: boolean | null;
  isQueued?: boolean | null;
  isPotentialError?: boolean | null;
  failedAt?: unknown;
  nextRunAt?: unknown;
  lastRunAt?: unknown;
  retries?: number | null;
  lastFailReason?: string | null;
  publishStoryManually?: boolean | null;
  storyType?: string | null;
  isNotification?: boolean | null;
}

/** A post carrying an already-computed server status plus the raw fields. */
export type PublishingStatusPost = PublishingStatusInput & {
  publishingStatus?: PublishingStatus | string | null;
};

export const DELAYED_THRESHOLD_MS = 60 * 60 * 1000;

export const IN_FLIGHT_STATUSES: ReadonlySet<PublishingStatus> =
  new Set<PublishingStatus>([
    "queued_publishing",
    "posting",
    "retrying",
    "manual_publishing",
    "delayed",
  ]);

const ALL_STATUSES: ReadonlySet<string> = new Set<PublishingStatus>([
  "scheduled",
  "queued_publishing",
  "posting",
  "retrying",
  "delayed",
  "manual_publishing",
  "posted",
  "imported",
  "failed",
  "potential_error",
  "canceled",
]);

export function isInFlight(status: PublishingStatus): boolean {
  return IN_FLIGHT_STATUSES.has(status);
}

export function isTerminallyFailed(status: PublishingStatus): boolean {
  return status === "failed";
}

function toMs(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === "number") return Number.isNaN(value) ? null : value;
  if (typeof value === "string") {
    const t = Date.parse(value);
    return Number.isNaN(t) ? null : t;
  }
  // Date or moment-like: valueOf() yields epoch ms.
  if (
    typeof value === "object" &&
    typeof (value as { valueOf?: unknown }).valueOf === "function"
  ) {
    const t = (value as { valueOf(): unknown }).valueOf();
    return typeof t === "number" && !Number.isNaN(t) ? t : null;
  }
  return null;
}

function isStale(lastRunAt: unknown, now: number): boolean {
  const last = toMs(lastRunAt);
  return last != null && now - last > DELAYED_THRESHOLD_MS;
}

/**
 * Raw-flag derivation. First match wins. MUST match the server decision table.
 */
function deriveFromRawFlags(
  post: PublishingStatusInput,
  now: number
): PublishingStatus {
  const {
    isCanceled,
    isImported,
    isSuccess,
    isPosting,
    isQueued,
    publishStoryManually,
    storyType,
    lastFailReason,
  } = post;
  const nextRunAt = toMs(post.nextRunAt);
  const failedAt = toMs(post.failedAt);
  const retries = post.retries ?? 0;
  // Run-evidence (NOT age): the terminal error states require the post to have run.
  const hasRun = toMs(post.lastRunAt) != null;

  // 0 — a draft never entered the publish pipeline: always benign, never an error.
  if (post.isDraft === true) return "scheduled";
  // 1
  if (isCanceled === true) return "canceled";
  // 2
  if (isImported === true) return "imported";
  // 3 — a successful-but-suspect post keeps its "may not have published" warning.
  if (isSuccess === true && post.isPotentialError === true) {
    return "potential_error";
  }
  if (isSuccess === true) return "posted";
  // 4
  if (failedAt != null) return "failed";
  // 4b — an explicit potential-error flag is honored regardless of lastRunAt; the
  // never-ran gate below only suppresses the flagless fallthrough (drafts / fresh).
  if (post.isPotentialError === true) return "potential_error";
  // 5 — an isSuccess:false orphan is only a failure if it actually ran; a fresh
  // post is stamped isSuccess:false at creation, so without lastRunAt it hasn't run.
  if (
    isSuccess === false &&
    !isQueued &&
    !isPosting &&
    nextRunAt == null &&
    hasRun
  ) {
    return "failed";
  }
  // 6
  if (
    isQueued &&
    !isPosting &&
    (publishStoryManually === true ||
      post.isNotification === true ||
      storyType === "reel" ||
      storyType === "igtv")
  ) {
    return "manual_publishing";
  }
  // 7
  if (isPosting) {
    return isStale(post.lastRunAt, now) ? "delayed" : "posting";
  }
  // 8
  if (isQueued && (nextRunAt == null || nextRunAt <= now)) {
    return isStale(post.lastRunAt, now) ? "delayed" : "queued_publishing";
  }
  // 9
  if (isQueued && nextRunAt != null && nextRunAt > now) return "retrying";
  // 10
  if (nextRunAt != null && (retries > 0 || lastFailReason != null)) {
    return "retrying";
  }
  // 11
  if (nextRunAt != null) return "scheduled";
  // 12 & 13 — an orphan that actually RAN is "needs attention"; one that never ran
  // (draft / not-yet-scheduled / just-created) is benign.
  if (hasRun) return "potential_error";
  return "scheduled";
}

/**
 * Resolve a post's publishing status.
 *
 * When the server has stamped a recognized `publishingStatus`, we START from it
 * and apply only the client-side "delayed" upgrade so the state stays live
 * between refetches. Otherwise we fall back to raw-flag derivation.
 */
export function getPublishingStatus(
  post: PublishingStatusPost,
  opts?: { now?: number }
): PublishingStatus {
  const now = opts?.now ?? Date.now();
  // A draft is benign regardless of any stray server-stamped field.
  if (post.isDraft === true) return "scheduled";
  const server = post.publishingStatus;

  if (typeof server === "string" && ALL_STATUSES.has(server)) {
    const status = server as PublishingStatus;
    // Only in-flight-but-fresh statuses can flip to delayed client-side.
    if (
      (status === "posting" || status === "queued_publishing") &&
      isStale(post.lastRunAt, now)
    ) {
      return "delayed";
    }
    return status;
  }

  return deriveFromRawFlags(post, now);
}

/**
 * Whether a post can be pulled out of the publishing queue ("Cancel publishing"):
 * a fresh `queued_publishing` post, or any post that has gone `delayed` — including
 * one stuck `delayed` while `isPosting` on a device. A `delayed` post is stale by
 * definition (in-flight for more than DELAYED_THRESHOLD_MS), so the window where a
 * device is *actively* mid-publish has effectively closed. A *fresh* `posting` post is
 * still excluded (double-post risk), as are `manual`/done; `delayed` never overlaps
 * `manual_publishing`, so this does not enable cancelling manual stories. Mirrors the
 * server's `isRemovableFromQueue` in publishingStatus.ts.
 */
export function isRemovableFromQueue(
  post: PublishingStatusPost,
  opts?: { now?: number }
): boolean {
  const status = getPublishingStatus(post, opts);
  return status === "queued_publishing" || status === "delayed";
}

/**
 * Attention severity of a status (higher = more urgent). Distinct from the
 * lifecycle enum order — an error/in-flight state must outrank a clean "posted"
 * so a multi-account card never reads green while a sibling child is stuck.
 */
const STATUS_SEVERITY: Record<PublishingStatus, number> = {
  failed: 100,
  potential_error: 90,
  delayed: 80,
  retrying: 70,
  posting: 60,
  queued_publishing: 50,
  manual_publishing: 40,
  scheduled: 30,
  posted: 20,
  imported: 10,
  canceled: 0,
};

/**
 * Worst-child-wins reducer for a multi-account post. Given the sibling child docs
 * of one clustered post (same `postClusterId`, one per account), returns the status
 * that most demands attention, so the card reflects a stuck IG child (`delayed`/
 * `posting`) instead of showing the green "P" of an already-posted FB sibling.
 * Returns `undefined` for an empty/absent list (caller should fall back to the
 * single post). Null-safe: skips missing entries and unknown statuses.
 */
export function getWorstPublishingStatus(
  posts:
    readonly (PublishingStatusPost | null | undefined)[] | null | undefined,
  opts?: { now?: number }
): PublishingStatus | undefined {
  if (!posts) return undefined;
  let worst: PublishingStatus | undefined;
  let worstRank = -Infinity;
  for (const p of posts) {
    if (!p) continue;
    const s = getPublishingStatus(p, opts);
    const rank = STATUS_SEVERITY[s] ?? 0;
    if (rank > worstRank) {
      worstRank = rank;
      worst = s;
    }
  }
  return worst;
}
